// src/components/OnboardingTutorial.tsx
// Interactive onboarding tutorial that shows users around the app

import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonContent,
  IonButton,
  IonIcon,
  IonText,
} from '@ionic/react';
import {
  close,
  arrowForward,
  arrowBack,
  checkmarkCircle,
} from 'ionicons/icons';
import './OnboardingTutorial.css';

export interface TutorialStep {
  title: string;
  description: string;
  targetElement?: string; // CSS selector for element to highlight
  position?: 'top' | 'bottom' | 'center';
  tab?: string; // Which tab this step is for (tab1, tab2, tab3, tab4)
}

interface OnboardingTutorialProps {
  steps: TutorialStep[];
  onComplete: () => void;
  onSkip: () => void;
  onTabChange?: (tab: string) => void;
}

export const OnboardingTutorial: React.FC<OnboardingTutorialProps> = ({
  steps,
  onComplete,
  onSkip,
  onTabChange,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isOpen, setIsOpen] = useState(true);

  // Navigate to the correct tab when step changes
  useEffect(() => {
    const step = steps[currentStep];
    if (step.tab && onTabChange) {
      onTabChange(step.tab);
    }
  }, [currentStep, steps, onTabChange]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    setIsOpen(false);
    onSkip();
  };

  const handleComplete = () => {
    setIsOpen(false);
    onComplete();
  };

  const step = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <IonModal
      isOpen={isOpen}
      backdropDismiss={false}
      className="onboarding-modal"
    >
      <div className="onboarding-content">
        {/* Skip button */}
        <div className="onboarding-header">
          <IonButton
            fill="clear"
            size="small"
            onClick={handleSkip}
            className="skip-button"
          >
            Skip Tutorial
          </IonButton>
        </div>

        {/* Progress bar */}
        <div className="progress-container">
          <div
            className="progress-bar"
            style={{
              width: `${progress}%`
            }}
          />
        </div>

        {/* Tutorial content */}
        <div className="tutorial-content">
          {/* Icon/Illustration */}
          <div className="tutorial-icon">
            {currentStep === steps.length - 1 ? (
              <IonIcon
                icon={checkmarkCircle}
                className="success-icon"
              />
            ) : (
              <div className="step-number">
                {currentStep + 1}
              </div>
            )}
          </div>

          {/* Step info */}
          <h2 className="tutorial-title">
            {step.title}
          </h2>
          <p className="tutorial-description">
            {step.description}
          </p>

          {/* Step counter */}
          <p className="step-counter">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>

        {/* Navigation buttons */}
        <div className="tutorial-actions">
          {currentStep > 0 && (
            <IonButton
              fill="outline"
              onClick={handleBack}
              className="back-button"
            >
              <IonIcon icon={arrowBack} slot="start" />
              Back
            </IonButton>
          )}

          <IonButton
            fill="solid"
            onClick={handleNext}
            className="next-button"
            expand={currentStep === 0 ? 'block' : undefined}
          >
            {currentStep === steps.length - 1 ? (
              <>
                Get Started
                <IonIcon icon={checkmarkCircle} slot="end" />
              </>
            ) : (
              <>
                Next
                <IonIcon icon={arrowForward} slot="end" />
              </>
            )}
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
};

// Hook to manage onboarding state
export const useOnboarding = () => {
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true);

  useEffect(() => {
    const completed = localStorage.getItem('onboarding_completed');
    // If not set (null), assume existing user has completed it
    // Only show if explicitly set to 'false'
    setHasCompletedOnboarding(completed !== 'false');
  }, []);

  const markOnboardingComplete = () => {
    localStorage.setItem('onboarding_completed', 'true');
    setHasCompletedOnboarding(true);
  };

  const resetOnboarding = () => {
    localStorage.setItem('onboarding_completed', 'false');
    setHasCompletedOnboarding(false);
  };

  return {
    hasCompletedOnboarding,
    markOnboardingComplete,
    resetOnboarding,
  };
};

// Default tutorial steps for Dice Cat
export const defaultOnboardingSteps: TutorialStep[] = [
  {
    title: '🎲 Welcome to Dice Cat!',
    description: 'Find local board game and tabletop RPG players who share your gaming interests. Let\'s show you around!',
    position: 'center',
  },
  {
    title: '🔍 Discover Players',
    description: 'Swipe through profiles to find players near you. Swipe right to connect, left to pass. Check out their game libraries and gaming preferences!',
    position: 'center',
    tab: 'tab1',
  },
  {
    title: '💬 Chat & Connect',
    description: 'When you match with someone, you can chat here! Send messages, share images, and plan your gaming sessions.',
    position: 'center',
    tab: 'tab2',
  },
  {
    title: '👥 Groups & Quests',
    description: "Create or join groups to chat with multiple players. Try the Pip's Guild Quest minigame for cooperative story adventures!",
    position: 'center',
    tab: 'tab2',
  },
  {
    title: '🎮 Mini Games',
    description: 'Play quick games like Nerdle, Sanctuary, or our old-school text adventure, Dungeon Escape while waiting for matches. Test your skills and have fun!',
    position: 'center',
    tab: 'tab4',
  },
  {
    title: '👤 Your Profile',
    description: 'Customize your profile with your favorite games, gaming preferences, and a cool meeple avatar. Make a great first impression!',
    position: 'center',
    tab: 'tab3',
  },
  {
    title: '♿ Accessibility Settings',
    description: 'Check out Account Settings for free accessibility features including colorblind themes, reduced motion, enhanced focus, and more!',
    position: 'center',
    tab: 'tab3',
  },
  {
    title: '🌟 Premium Features',
    description: 'Upgrade to Premium for unlimited swipes, see who liked you, rewind accidental passes, and boost your profile visibility!',
    position: 'center',
    tab: 'tab3',
  },
  {
    title: '✅ You\'re Ready!',
    description: 'Start swiping, matching, and connecting with your local gaming community. Have fun and happy gaming! 🎲',
    position: 'center',
  },
];
